import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function Employees() {
  const t = useT();
  const [employees, setEmployees] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.listEmployees().then(setEmployees).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.createEmployee(name);
      setNewName("");
      await load();
      toast.success("Employee added");
    } catch {
      toast.error("Could not add employee");
    }
  };

  const saveEdit = async () => {
    const name = editName.trim();
    if (!name) return;
    try {
      await api.updateEmployee(editId, name);
      setEditId(null);
      setEditName("");
      await load();
      toast.success("Employee updated");
    } catch {
      toast.error("Could not update");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteEmployee(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      toast.success("Employee removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">
          {t("nav_employees")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("employees_subtitle")}
        </p>
      </div>

      <Card className="rounded-2xl border-border p-6">
        <div className="flex gap-3">
          <Input
            placeholder={t("new_employee_name")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid="new-employee-input"
            className="h-12 flex-1 text-base"
          />
          <Button onClick={add} data-testid="add-employee-btn" className="h-12 rounded-xl px-5">
            <Plus className="mr-1 h-5 w-5" /> {t("add")}
          </Button>
        </div>
      </Card>

      {employees.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-2xl border-dashed border-border p-12 text-center">
          <Users className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("no_employees_add")}</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="employees-list">
          {employees.map((e) => (
            <Card key={e.id} className="flex items-center justify-between rounded-2xl border-border p-4">
              {editId === e.id ? (
                <div className="flex flex-1 gap-2">
                  <Input
                    value={editName}
                    onChange={(ev) => setEditName(ev.target.value)}
                    onKeyDown={(ev) => ev.key === "Enter" && saveEdit()}
                    data-testid={`edit-employee-input-${e.id}`}
                    className="h-10 flex-1"
                    autoFocus
                  />
                  <Button size="icon" className="h-10 w-10" onClick={saveEdit} data-testid={`save-employee-${e.id}`}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" className="h-10 w-10" onClick={() => setEditId(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="font-display text-lg font-bold text-foreground">{e.name}</span>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-10 w-10 text-muted-foreground"
                      onClick={() => {
                        setEditId(e.id);
                        setEditName(e.name);
                      }}
                      data-testid={`edit-employee-${e.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-10 w-10 text-muted-foreground hover:text-rose-600"
                      onClick={() => setDeleteTarget(e)}
                      data-testid={`delete-employee-${e.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("remove")} {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t("remove_employee_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-employee-btn">
              {t("remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
